import {
    // eslint-disable-next-line no-restricted-imports
    useMutation,
    UseMutationOptions,
    // eslint-disable-next-line no-restricted-imports
    useQuery as innerUseQuery,
    UseQueryResult,
    type UseQueryOptions,
} from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { useAuthorizationContext, useClientPortalConfigContext } from "../context";
import { useClientPortalNavigate } from "./useClientPortalRouter";
import { ErrorPropsBase, handleAxiosError } from "./useClientPortalQuery.helpers";
import { getRecaptchaToken, RECAPTCHA_TOKEN_HEADER, RecaptchaActionName } from "../utils/recaptcha";

/**
 * A missing token is not sent as an empty header: the server refuses the request either way, and
 * leaving it off keeps the refusal on the server's single code path.
 */
async function botProtectionHeaders(siteKey: string | null | undefined, action: RecaptchaActionName | undefined) {
    if (!siteKey || !action) {
        return undefined;
    }

    const token = await getRecaptchaToken(siteKey, action);

    return token ? { [RECAPTCHA_TOKEN_HEADER]: token } : undefined;
}

function useDefaultHandleAxiosErrorProps(): ErrorPropsBase {
    const { logoutUser, userDetails } = useAuthorizationContext();
    const navigateTo = useClientPortalNavigate();
    const { clientPortalGuid } = useClientPortalConfigContext();

    return {
        logoutUser,
        navigateTo,
        clientPortalGuid,
        userLoggedIn: !!userDetails,
    } as ErrorPropsBase;
}

/**
 * useQuery is used to fetch data from the server (GET requests).
 *
 * @param url - path for the GET request.
 * @param options - options for the query.
 * @param handleCustomError - optional custom error handler function.
 */
function useGetQuery<TQueryFnData = unknown, TData = TQueryFnData>({
    url,
    options,
    handleCustomError,
}: {
    url: string;
    options?: Omit<UseQueryOptions<TData, Error>, "queryKey">;
    handleCustomError?: (error: AxiosError) => void;
}): UseQueryResult<TData, Error> {
    const errorProps = useDefaultHandleAxiosErrorProps();
    return innerUseQuery({
        ...options,
        queryKey: [url],
        queryFn: async () => {
            try {
                const response = await axios.get<TData>(url);
                return response.data;
            } catch (error) {
                if (handleCustomError) {
                    handleCustomError(error as AxiosError);
                } else {
                    handleAxiosError({
                        ...errorProps,
                        error: error as AxiosError,
                    });
                }
                throw error;
            }
        },
    });
}

/**
 * usePostMutation is used to send data to the server (POST requests).
 * @param url
 * @param options - options for the mutation.
 * @param handleCustomError - optional custom error handler function.
 * @param recaptchaAction - set it on endpoints carrying [RequireBotProtection] to mint a reCAPTCHA
 * token for the same action on submit. Ignored for practices without bot protection.
 */
function usePostMutation<TData = unknown, TError = unknown, TVariables = void>({
    url,
    options,
    handleCustomError,
    recaptchaAction,
}: {
    url: string;
    options?: UseMutationOptions<TData, TError, TVariables>;
    handleCustomError?: (error: AxiosError) => void;
    recaptchaAction?: RecaptchaActionName;
}) {
    const errorProps = useDefaultHandleAxiosErrorProps();
    const { clientPortalConfig } = useClientPortalConfigContext();
    const reCaptchaSiteKey = clientPortalConfig?.reCaptchaSiteKey;

    return useMutation<TData, TError, TVariables>({
        ...options,
        mutationFn: async (variables) => {
            try {
                const response = await axios.post<TData>(url, variables, {
                    headers: await botProtectionHeaders(reCaptchaSiteKey, recaptchaAction),
                });
                return response.data;
            } catch (error) {
                if (handleCustomError) {
                    handleCustomError(error as AxiosError);
                } else {
                    handleAxiosError({
                        ...errorProps,
                        error: error as AxiosError,
                    });
                }
                throw error;
            }
        },
    });
}

/**
 * usePutMutation is used to update data on the server (PUT requests).
 * @param url
 * @param options - options for the mutation.
 * @param handleCustomError - optional custom error handler function.
 */
function usePutMutation<TData = unknown, TError = unknown, TVariables = void>({
    url,
    options,
    handleCustomError,
}: {
    url: string;
    options?: UseMutationOptions<TData, TError, TVariables>;
    handleCustomError?: (error: AxiosError) => void;
}) {
    const errorProps = useDefaultHandleAxiosErrorProps();

    return useMutation<TData, TError, TVariables>({
        ...options,
        mutationFn: async (variables) => {
            try {
                const response = await axios.put<TData>(url, variables);
                return response.data;
            } catch (error) {
                if (handleCustomError) {
                    handleCustomError(error as AxiosError);
                } else {
                    handleAxiosError({
                        ...errorProps,
                        error: error as AxiosError,
                    });
                }
                throw error;
            }
        },
    });
}

/**
 * useDeleteMutation is used to delete data on the server (DELETE requests).
 * @param url
 * @param options - options for the mutation.
 * @param handleCustomError - optional custom error handler function.
 */
function useDeleteMutation<TData = unknown, TError = unknown, TVariables extends string | number = number>({
    url,
    options,
    handleCustomError,
}: {
    url: string;
    options?: UseMutationOptions<TData, TError, TVariables>;
    handleCustomError?: (error: AxiosError) => void;
}) {
    const errorProps = useDefaultHandleAxiosErrorProps();

    return useMutation<TData, TError, TVariables>({
        ...options,
        mutationFn: async (id: TVariables) => {
            try {
                const response = await axios.delete<TData>(`${url}${id}`);
                return response.data;
            } catch (error) {
                if (handleCustomError) {
                    handleCustomError(error as AxiosError);
                } else {
                    handleAxiosError({
                        ...errorProps,
                        error: error as AxiosError,
                    });
                }

                throw error;
            }
        },
    });
}

function is400Error(error: AxiosError): boolean {
    return error.response?.status >= 400 && error.response?.status < 500;
}

export { useGetQuery, usePostMutation, usePutMutation, useDeleteMutation, is400Error, useDefaultHandleAxiosErrorProps };
